import React from 'react';
import { Flowbite } from 'flowbite-react';
import { HelmetProvider } from '@dr.pogodin/react-helmet';
import AppRoutes from './routes';
import { AuthProvider } from './contexts/AuthContext';
// StripeProvider removed — no component uses useStripeContext().
// All Stripe consumers call loadStripe() directly at component level,
// so the global provider was only adding a 217KB eager download on every page.
import { ThemeProvider } from './contexts/ThemeContext';
import { PublicSettingsProvider } from './contexts/PublicSettingsContext';
import { ToastContainer } from 'react-toastify';
import MaintenancePage from './pages/MaintenancePage';
import ComingSoon from './pages/ComingSoon';
import { lazyWithRetry } from './utils/lazyWithRetry';
const CookieConsent = lazyWithRetry(() => import('./components/common/CookieConsent'));
const VersionChecker = lazyWithRetry(() => import('./components/shared/VersionChecker'));
const ChatWidget = lazyWithRetry(() => import('./components/chat/ChatWidget'));

// Custom Flowbite theme
const theme = {
  button: {
    color: {
      primary: 'bg-primary-600 hover:bg-primary-700 text-white',
    },
  },
  toggleSwitch: {
    toggle: {
      checked: {
        color: {
          blue: 'bg-primary-600 border-primary-600',
        },
      },
    },
  },
};

function App() {
  // Lazy-load ReactToastify CSS after first render to avoid render-blocking
  React.useEffect(() => {
    import('react-toastify/dist/ReactToastify.css');
  }, []);

  // Capture signup access code from URL (?access=CODE) and store in sessionStorage
  // so it persists through OAuth redirects within the same tab
  React.useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const accessCode = params.get('access');
    if (accessCode) {
      sessionStorage.setItem('signup_access_code', accessCode);
    }
  }, []);

  // Check for coming soon mode (takes priority over maintenance mode)
  // When enabled, only the root path shows Coming Soon - all other pages work normally
  const isComingSoonMode = import.meta.env.VITE_COMING_SOON_MODE === 'true';
  const currentPath = window.location.pathname;

  // If coming soon mode is enabled and we're on the root path, show coming soon page
  // All other pages (/dashboard, /login, /admin, /privacy, etc.) work normally
  if (isComingSoonMode && currentPath === '/') {
    return <ComingSoon />;
  }

  // Check for maintenance mode
  const isMaintenanceMode = import.meta.env.VITE_MAINTENANCE_MODE === 'true';

  // If maintenance mode is enabled, show maintenance page
  if (isMaintenanceMode) {
    return <MaintenancePage />;
  }

  return (
    <HelmetProvider>
      <PublicSettingsProvider>
        <ThemeProvider>
          <Flowbite theme={{ theme }}>
            <AuthProvider>
                <React.Suspense fallback={null}>
                  <VersionChecker />
                </React.Suspense>
                <AppRoutes />
                <ToastContainer
                  position="top-right"
                  autoClose={3000}
                  hideProgressBar={false}
                  newestOnTop={true}
                  closeOnClick={true}
                  rtl={false}
                  pauseOnFocusLoss
                  draggable
                  pauseOnHover
                  theme="colored"
                  limit={3}
                  style={{
                    zIndex: 9999,
                  }}
                />
                <React.Suspense fallback={null}>
                  <ChatWidget />
                </React.Suspense>
                <React.Suspense fallback={null}>
                  <CookieConsent />
                </React.Suspense>
            </AuthProvider>
          </Flowbite>
        </ThemeProvider>
      </PublicSettingsProvider>
    </HelmetProvider>
  );
}

export default App;